import React, { useRef, useEffect, useState } from 'react';
import { BoxLogo, BoxJobitLogo, SalesforceLogo, TableauLogo, JoWebsiteLogo, ComposeModeLogo, AtlasChatLogo, ImageGenerationLogo, WebSearchLogo } from './BrandLogos';
import atlasDeepInsightsLogo from '../../../Assets/atlas_deep_insights_logo.svg';
import { LlmProvider, ModelType } from '../hooks/useModelSelector';
import ModelPicker, { MODEL_TYPE_LABELS } from './ModelPicker';
import { MAX_CHAT_INPUT_CHARS } from '../constants';

export const FOCUS_SUPPORTED = new Set(['box', 'salesforce', 'tableau']);

// The character counter only appears once the draft gets close to the limit.
const CHAR_COUNT_VISIBLE_FROM = Math.floor(MAX_CHAT_INPUT_CHARS * 0.9);

// One model picker to show in the input bar (see ModelPicker).
export interface ModelPickerConfig {
  type: ModelType;
  providers: LlmProvider[];
  selectedModelId: string | null;
  lockedProviderId: string | null;
}

const SourceIcon: React.FC<{ value: string; icon?: string; color?: string }> = ({ value, icon, color }) => {
  if (value === 'box')          return <BoxLogo />;
  if (value === 'box-jobit')    return <BoxJobitLogo />;
  if (value === 'salesforce')   return <SalesforceLogo />;
  if (value === 'tableau')      return <TableauLogo />;
  if (value === 'jo-website')   return <JoWebsiteLogo />;
  if (value === 'atlas')        return <AtlasChatLogo />;
  if (value === 'image_generation') return <ImageGenerationLogo />;
  return <i className={icon} aria-hidden="true" style={{ color }} />;
};

interface SourceOption {
  value: string;
  label: string;
  // Only 'all' has no dedicated logo in SourceIcon, so it's the only entry
  // that needs a fallback icon/color — every other value renders its own logo.
  icon?: string;
  color?: string;
}

// The combinable multi-select set. 'all' is a reset action (not a member of
// selectedSources), and Compose is its own separate exclusive mode — neither
// appears here; both are rendered/handled independently below.
export const SOURCES: SourceOption[] = [
  { value: 'all',          label: 'All',                  icon: 'fas fa-layer-group', color: '#6b7280' },
  { value: 'atlas',        label: 'Atlas Lead Intelligence' },
  { value: 'box',          label: 'Box Sharing' },
  { value: 'box-jobit',    label: 'JOBIT Talking Points' },
  { value: 'salesforce',   label: 'Salesforce' },
  { value: 'tableau',      label: 'Tableau Dashboard' },
  { value: 'jo-website',   label: 'JobsOhio Website' },
  { value: 'image_generation', label: 'Create Image' },
];

// Hover text shown beside each + menu item. Keyed by the SOURCES value, or
// by the item's own key for the entries rendered outside SOURCES.
const MODE_MENU_HINTS: Record<string, string> = {
  all: 'Search every connected source.',
  atlas: 'Research companies and leads.',
  box: 'Search files in Box.',
  'box-jobit': 'Find approved talking points.',
  salesforce: 'Look up Salesforce records (Projects & Program tables).',
  tableau: 'Ask questions about Tableau data.',
  'jo-website': 'Search jobsohio.com content.',
  compose: 'Draft and refine written content.',
  image_generation: 'Generate images from a description.',
  'file-upload': 'Ask questions about an attached file.',
  'web-search': 'Add current info from the web.',
  'deep-search': 'Superintelligence search across all sources. Takes longer to run.',
};

const modeHintId = (item: string) => `mode-menu-hint-${item}`;

// The hover text itself, rendered inside its item and shown beside the menu
// on hover/keyboard focus (CSS: .mode-item-tooltip). Hidden from assistive
// tech here — the item points at it with aria-describedby instead, so it's
// read as the item's description without becoming part of its name.
const ModeItemHint: React.FC<{ item: string }> = ({ item }) => {
  const text = MODE_MENU_HINTS[item];
  if (!text) return null;
  return (
    <span id={modeHintId(item)} className="mode-item-tooltip" aria-hidden="true">
      {text}
    </span>
  );
};

interface ChatInputProps {
  inputMessage: string;
  isLoading: boolean;
  deepSearch: boolean;
  webSearch: boolean;
  composeMode: boolean;
  selectedSources: Set<string>;
  isModeMenuOpen: boolean;
  messagesLength: number;
  modeMenuRef: React.RefObject<HTMLDivElement | null>;
  modeMenuTriggerRef: React.RefObject<HTMLButtonElement | null>;
  fileUploadMode: boolean;
  stagedFileCount: number;
  doneFileCount: number;
  atlasEnabled: boolean;
  atlasDeepInsights: boolean;
  isAtlasModeMenuOpen: boolean;
  atlasModeMenuRef: React.RefObject<HTMLDivElement | null>;
  atlasModeMenuTriggerRef: React.RefObject<HTMLButtonElement | null>;
  // The model pickers to show, top to bottom (useModelSelector's
  // getModelPickerTypes).
  modelPickers: ModelPickerConfig[];
  modelsStatus: 'loading' | 'ready' | 'error';
  openModelMenu: ModelType | null;
  modelMenuRef: React.RefObject<HTMLDivElement | null>;
  modelMenuTriggerRef: React.RefObject<HTMLButtonElement | null>;
  onInputChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onStop: () => void;
  onToggleModeMenu: () => void;
  onToggleDeepSearch: () => void;
  onToggleWebSearch: () => void;
  onToggleCompose: () => void;
  onSourceToggle: (value: string) => void;
  onTextareaFocus: () => void;
  onToggleFileUpload: () => void;
  onSelectModel: (type: ModelType, modelId: string) => void;
  onToggleModelMenu: (type: ModelType) => void;
  onRetryModels: () => void;
  onToggleAtlasModeMenu: () => void;
  onSelectAtlasMode: (mode: 'normal' | 'deep_insights') => void;
  onReopenFocus: (source: string) => void;
  onReopenFileUpload: () => void;
  focusSelectionNames: Record<string, string | null>;
}

const ChatInput: React.FC<ChatInputProps> = ({
  inputMessage,
  isLoading,
  deepSearch,
  webSearch,
  composeMode,
  selectedSources,
  isModeMenuOpen,
  messagesLength,
  modeMenuRef,
  modeMenuTriggerRef,
  fileUploadMode,
  stagedFileCount,
  doneFileCount,
  atlasEnabled,
  atlasDeepInsights,
  isAtlasModeMenuOpen,
  atlasModeMenuRef,
  atlasModeMenuTriggerRef,
  modelPickers,
  modelsStatus,
  openModelMenu,
  modelMenuRef,
  modelMenuTriggerRef,
  onInputChange,
  onSubmit,
  onStop,
  onToggleModeMenu,
  onToggleDeepSearch,
  onToggleWebSearch,
  onToggleCompose,
  onSourceToggle,
  onTextareaFocus,
  onToggleFileUpload,
  onSelectModel,
  onToggleModelMenu,
  onRetryModels,
  onToggleAtlasModeMenu,
  onSelectAtlasMode,
  onReopenFocus,
  onReopenFileUpload,
  focusSelectionNames,
}) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Set when a paste overflowed maxLength — the browser silently drops the
  // excess, so this is the only signal the user gets that text was cut off.
  const [pasteTrimmed, setPasteTrimmed] = useState(false);

  const selectedSourceList = Array.from(selectedSources);

  // A picker whose list has no models (none of that type) has nothing to
  // show, so it's left out — and doesn't count towards "stacked".
  const visibleModelPickers = modelPickers.filter((picker) => picker.selectedModelId !== null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading) {
        onSubmit(e as any);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    const pastedLength = e.clipboardData.getData('text').length;
    const replacedLength = el.selectionEnd - el.selectionStart;
    if (el.value.length - replacedLength + pastedLength > MAX_CHAT_INPUT_CHARS) {
      setPasteTrimmed(true);
    }
  };

  // Clear the "cut off" notice once the draft drops back under the limit
  // (text deleted, or the message was sent and the input cleared).
  useEffect(() => {
    if (inputMessage.length < MAX_CHAT_INPUT_CHARS) setPasteTrimmed(false);
  }, [inputMessage]);

  const adjustTextareaHeight = () => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 225)}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [inputMessage]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      inputRef.current?.focus();
    }
  }, [messagesLength, isLoading]);

  const placeholder = deepSearch
    ? 'Deep Search • Message JO Enterprise Chat…'
    : composeMode
    ? 'Compose • Message JO Enterprise Chat…'
    : selectedSourceList.length === 1
    ? `${SOURCES.find((s) => s.value === selectedSourceList[0])?.label} • Message JO Enterprise Chat…`
    : 'Message JO Enterprise Chat…';

  return (
    <form onSubmit={onSubmit} className="chat-input-form">
      <div className="input-container">
        <textarea
          ref={inputRef}
          className="message-input"
          value={inputMessage}
          onChange={(e) => onInputChange(e.target.value)}
          onFocus={onTextareaFocus}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          maxLength={MAX_CHAT_INPUT_CHARS}
          rows={1}
        />

        {(inputMessage.length >= CHAR_COUNT_VISIBLE_FROM || pasteTrimmed) && (
          <div
            className={`input-char-count ${inputMessage.length >= MAX_CHAT_INPUT_CHARS ? 'at-limit' : ''}`}
            aria-live="polite"
          >
            {pasteTrimmed && (
              <span>Pasted text was cut off at the {MAX_CHAT_INPUT_CHARS.toLocaleString()}-character limit · </span>
            )}
            {inputMessage.length.toLocaleString()} / {MAX_CHAT_INPUT_CHARS.toLocaleString()}
          </div>
        )}

        {/* Toolbar row — wraps as a whole unit: the left group (+ button +
            chips) wraps its own contents onto additional lines as sources
            pile up, while the right group (model selector + send button)
            stays a fixed size and only drops to its own line once the left
            group can no longer fit beside it. */}
        <div className="input-toolbar">
          <div className="input-adornments">
            {/* + button */}
            <button
              ref={modeMenuTriggerRef}
              type="button"
              className="plus-button"
              aria-haspopup="true"
              aria-expanded={isModeMenuOpen}
              aria-label="Add tools"
              onClick={onToggleModeMenu}
            >
              <i className="fas fa-plus" />
            </button>

            {/* One chip per selected source — any combination of Atlas/Box/
                JOBIT/Salesforce/Tableau/JobsOhio Website can be active at once. */}
            {!deepSearch && selectedSourceList.map((src) => {
              const opt = SOURCES.find((s) => s.value === src);
              if (!opt) return null;

              const isFocus = FOCUS_SUPPORTED.has(src);
              const isAtlas = src === 'atlas';

              // Atlas / Box-Jobit / JobsOhio Website: no picker on the chip —
              // plain chip, click anywhere removes it. Atlas's chat mode
              // (Normal vs Deep Insights) is chosen up front from the Source
              // list flyout, so the chip here just reflects it in the label.
              if (!isFocus) {
                return (
                  <button
                    key={src}
                    type="button"
                    className={`mode-chip source-chip source-chip--${src}`}
                    title={`Source: ${opt.label} — click to remove`}
                    onClick={() => onSourceToggle(src)}
                  >
                    <SourceIcon value={opt.value} icon={opt.icon} color={opt.color} />
                    <span>
                      {isAtlas ? 'Atlas' : opt.label}
                      {isAtlas && atlasDeepInsights ? ' · Deep Insights' : ''}
                    </span>
                    <i className="fas fa-times close" aria-hidden="true" />
                  </button>
                );
              }

              // Box / Salesforce / Tableau: main body reopens the Focus
              // picker modal, so the × to remove lives in its own button.
              return (
                <div key={src} className={`mode-chip source-chip source-chip--${src}`}>
                  <button
                    type="button"
                    className="source-chip-main"
                    title={`${opt.label} — click to open`}
                    onClick={() => onReopenFocus(src)}
                  >
                    <SourceIcon value={opt.value} icon={opt.icon} color={opt.color} />
                    <span>
                      {opt.label}
                      {focusSelectionNames[src] && (
                        <span className="source-chip-selection"> · {focusSelectionNames[src]}</span>
                      )}
                    </span>
                  </button>

                  <button
                    type="button"
                    className="source-chip-close"
                    title={`Source: ${opt.label} — click to remove`}
                    aria-label="Remove source"
                    onClick={() => onSourceToggle(src)}
                  >
                    <i className="fas fa-times close" aria-hidden="true" />
                  </button>
                </div>
              );
            })}

            {/* Deep search chip */}
            {deepSearch && (
              <button
                type="button"
                className="mode-chip deep-search"
                title="Deep Search is on — click to turn off"
                onClick={onToggleDeepSearch}
              >
                <i className="fas fa-magnifying-glass-plus" aria-hidden="true" />
                <span>Deep Search</span>
                <i className="fas fa-times close" aria-hidden="true" />
              </button>
            )}

            {/* Compose chip */}
            {composeMode && (
              <button
                type="button"
                className="mode-chip compose-chip"
                title="Compose is on — click to turn off"
                onClick={onToggleCompose}
              >
                <ComposeModeLogo />
                <span>Compose</span>
                <i className="fas fa-times close" aria-hidden="true" />
              </button>
            )}

            {/* File Upload chip — main body reopens the popup, × turns it off */}
            {fileUploadMode && (
              <div className="mode-chip file-upload-chip">
                <button
                  type="button"
                  className="source-chip-main"
                  title="Files — click to open"
                  onClick={onReopenFileUpload}
                >
                  <i className="fas fa-paperclip" aria-hidden="true" />
                  <span>
                    Files
                    {doneFileCount > 0 ? ` (${doneFileCount})` : ''}
                    {stagedFileCount > 0 ? ` · ${stagedFileCount} pending` : ''}
                  </span>
                </button>
                <button
                  type="button"
                  className="source-chip-close"
                  title="File Upload is on — click to turn off"
                  aria-label="Turn off file upload"
                  onClick={onToggleFileUpload}
                >
                  <i className="fas fa-times close" aria-hidden="true" />
                </button>
              </div>
            )}

            {/* Web Search chip */}
            {webSearch && (
              <button
                type="button"
                className="mode-chip web-search-chip"
                title="Web Search is on — click to turn off"
                onClick={onToggleWebSearch}
              >
                <WebSearchLogo />
                <span>Web Search</span>
                <i className="fas fa-times close" aria-hidden="true" />
              </button>
            )}

            {/* Mode / Source popup menu */}
            {isModeMenuOpen && (
              <div
                className={`mode-menu ${isAtlasModeMenuOpen ? 'atlas-flyout-open' : ''}`}
                ref={modeMenuRef}
                role="menu"
                aria-label="Select source"
              >
                <div className="mode-menu-section-label">Source</div>

                {SOURCES.map((s) => {
                  // Create Image is still a SOURCES entry (chips/placeholder look
                  // up its label/icon there), but it renders below, grouped with
                  // Compose/File Upload instead of in this list.
                  if (s.value === 'image_generation') return null;

                  const isAtlasDisabled = s.value === 'atlas' && !atlasEnabled;
                  const isChecked = s.value === 'all' ? selectedSources.size === 0 : selectedSources.has(s.value);

                  // Atlas Lead Intelligence: instead of a plain toggle, the
                  // row opens a nested flyout to pick which chat mode to use
                  // (Normal vs Deep Insights) — picking either one selects
                  // Atlas as a source in that mode, in a single step.
                  if (s.value === 'atlas') {
                    const isNormalActive = isChecked && !atlasDeepInsights;
                    const isDeepInsightsActive = isChecked && atlasDeepInsights;
                    return (
                      <div key={s.value} className="mode-item-atlas-wrap">
                        <button
                          ref={atlasModeMenuTriggerRef}
                          type="button"
                          aria-haspopup="true"
                          aria-expanded={isAtlasModeMenuOpen}
                          aria-disabled={isAtlasDisabled}
                          aria-describedby={modeHintId(s.value)}
                          disabled={isAtlasDisabled}
                          className={`mode-item ${isChecked ? 'active' : ''} ${isAtlasDisabled ? 'disabled' : ''}`}
                          onClick={() => {
                            if (isAtlasDisabled) return;
                            onToggleAtlasModeMenu();
                          }}
                        >
                          <SourceIcon value={s.value} icon={s.icon} color={s.color} />
                          <span>{s.label}</span>
                          <i className="fas fa-chevron-right mode-item-arrow" aria-hidden="true" />
                          <ModeItemHint item={s.value} />
                        </button>

                        {isAtlasModeMenuOpen && (
                          <div className="atlas-mode-menu" ref={atlasModeMenuRef} role="menu" aria-label="Atlas chat mode">
                            <button
                              type="button"
                              role="menuitemradio"
                              aria-checked={isNormalActive}
                              className={`mode-item atlas-submenu-item ${isNormalActive ? 'active' : ''}`}
                              onClick={() => onSelectAtlasMode('normal')}
                            >
                              <AtlasChatLogo />
                              <span className="atlas-submenu-text">
                                <span>Atlas</span>
                                <span className="atlas-submenu-desc">General lead and company info.</span>
                              </span>
                              {isNormalActive && <i className="fas fa-check check" aria-hidden="true" />}
                            </button>

                            <button
                              type="button"
                              role="menuitemradio"
                              aria-checked={isDeepInsightsActive}
                              className={`mode-item atlas-submenu-item ${isDeepInsightsActive ? 'active' : ''}`}
                              onClick={() => onSelectAtlasMode('deep_insights')}
                            >
                              <img src={atlasDeepInsightsLogo} alt="" width={16} height={16} style={{ objectFit: 'contain' }} />
                              <span className="atlas-submenu-text">
                                <span>Deep Insights</span>
                                <span className="atlas-submenu-desc">In-depth lead reports. Takes longer to run.</span>
                              </span>
                              {isDeepInsightsActive && <i className="fas fa-check check" aria-hidden="true" />}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <button
                      key={s.value}
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={isChecked}
                      aria-describedby={modeHintId(s.value)}
                      className={`mode-item ${isChecked ? 'active' : ''}`}
                      onClick={() => onSourceToggle(s.value)}
                    >
                      <SourceIcon value={s.value} icon={s.icon} color={s.color} />
                      <span>{s.label}</span>
                      {isChecked && <i className="fas fa-check check" aria-hidden="true" />}
                      <ModeItemHint item={s.value} />
                    </button>
                  );
                })}

                {/* Compose — exclusive: turning it on clears every source and every other mode */}
                <div className="mode-menu-divider" />

                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={composeMode}
                  aria-describedby={modeHintId('compose')}
                  className={`mode-item ${composeMode ? 'active' : ''}`}
                  onClick={onToggleCompose}
                >
                  <ComposeModeLogo />
                  <span>Compose</span>
                  {composeMode && <i className="fas fa-check check" aria-hidden="true" />}
                  <ModeItemHint item="compose" />
                </button>

                {/* Create Image — a combinable source like Box/Salesforce/etc.,
                    just grouped visually between Compose and File Upload */}
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={selectedSources.has('image_generation')}
                  aria-describedby={modeHintId('image_generation')}
                  className={`mode-item ${selectedSources.has('image_generation') ? 'active' : ''}`}
                  onClick={() => onSourceToggle('image_generation')}
                >
                  <SourceIcon value="image_generation" />
                  <span>Create Image</span>
                  {selectedSources.has('image_generation') && <i className="fas fa-check check" aria-hidden="true" />}
                  <ModeItemHint item="image_generation" />
                </button>

                {/* File Upload */}
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={fileUploadMode}
                  aria-describedby={modeHintId('file-upload')}
                  className={`mode-item ${fileUploadMode ? 'active' : ''}`}
                  onClick={onToggleFileUpload}
                >
                  <i className="fas fa-paperclip" aria-hidden="true" style={{ color: '#10b981' }} />
                  <span>File Upload</span>
                  {fileUploadMode && <i className="fas fa-check check" aria-hidden="true" />}
                  <ModeItemHint item="file-upload" />
                </button>

                {/* Web Search — fully agnostic: stays on alongside any other mode */}
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={webSearch}
                  aria-describedby={modeHintId('web-search')}
                  className={`mode-item ${webSearch ? 'active' : ''}`}
                  onClick={onToggleWebSearch}
                >
                  <WebSearchLogo />
                  <span>Web Search</span>
                  <span className={`mode-toggle-switch ${webSearch ? 'on' : ''}`} aria-hidden="true" />
                  <ModeItemHint item="web-search" />
                </button>

                {/* Deep Search — always visible; auto-selects 'all' when enabled */}
                <div className="mode-menu-divider" />
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={deepSearch}
                  aria-describedby={modeHintId('deep-search')}
                  className={`mode-item ${deepSearch ? 'active deep-search-item' : ''}`}
                  onClick={onToggleDeepSearch}
                >
                  <i className="fas fa-magnifying-glass-plus" aria-hidden="true" style={{ color: '#6366f1' }} />
                  <span>Deep Search</span>
                  <span className="mode-item-hint">All sources</span>
                  {deepSearch && <i className="fas fa-check check" aria-hidden="true" />}
                  <ModeItemHint item="deep-search" />
                </button>
              </div>
            )}
          </div>

          <div className="input-adornments-right">
            {/* Model selector */}
            {modelsStatus === 'loading' && (
              <button type="button" className="model-trigger-button loading" disabled aria-label="Loading models">
                <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                <span>Loading models…</span>
              </button>
            )}

            {modelsStatus === 'error' && (
              <button
                type="button"
                className="model-trigger-button error"
                onClick={onRetryModels}
                title="Failed to load models — click to retry"
              >
                <i className="fas fa-triangle-exclamation" aria-hidden="true" />
                <span>Retry</span>
              </button>
            )}

            {/* One picker per model type in play, top to bottom — image above
                chat when both show. Labelled only while stacked, so a lone
                picker looks as it always has. */}
            {modelsStatus === 'ready' && visibleModelPickers.length > 0 && (
              <div className="model-pickers">
                {visibleModelPickers.map((picker) => (
                  <ModelPicker
                    key={picker.type}
                    label={visibleModelPickers.length > 1 ? MODEL_TYPE_LABELS[picker.type] : undefined}
                    providers={picker.providers}
                    selectedModelId={picker.selectedModelId}
                    lockedProviderId={picker.lockedProviderId}
                    isOpen={openModelMenu === picker.type}
                    menuRef={modelMenuRef}
                    triggerRef={modelMenuTriggerRef}
                    onToggle={() => onToggleModelMenu(picker.type)}
                    onSelect={(modelId) => onSelectModel(picker.type, modelId)}
                  />
                ))}
              </div>
            )}

            {isLoading ? (
              <button
                type="button"
                className="stop-button"
                onClick={onStop}
                aria-label="Stop generating"
                title="Stop generating"
              >
                <i className="fas fa-stop"></i>
              </button>
            ) : (
              <button
                type="submit"
                className={`send-button ${inputMessage.trim() ? 'active' : ''}`}
                disabled={!inputMessage.trim()}
                aria-label="Send message"
              >
                <i className="fas fa-arrow-up"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    </form>
  );
};

export default ChatInput;
